import React, { useEffect } from 'react';
import { Quotation } from '../../types/quotation';
import { useOrganizations } from '../../hooks/useOrganizations';
import { useCustomers } from '../../hooks/useCustomers';
import { useProducts } from '../../hooks/useProducts';
import Modal from '../common/Modal';
import { format } from 'date-fns';
import {
  DocumentTextIcon,
  DocumentIcon,
  PencilSquareIcon
} from '@heroicons/react/24/outline';
import { useLanguage } from '../../contexts/LanguageContext';

interface QuotationViewModalProps {
  isOpen: boolean;
  onClose: () => void;
  quotation: Quotation | null;
  onEdit: (quotation: Quotation) => void;
}

const QuotationViewModal: React.FC<QuotationViewModalProps> = ({
  isOpen,
  onClose,
  quotation,
  onEdit
}) => {
  const { organizations } = useOrganizations();
  const { customers } = useCustomers();
  const { products } = useProducts();
  const { t } = useLanguage();

  // Komponens betöltésekor ellenőrizzük az adatokat
  useEffect(() => {
    if (quotation) {
      console.log("QuotationViewModal - kapott ajánlat adatok:", quotation);
    }
  }, [quotation]);

  // Biztonságos dátum formázó függvény
  const formatSafeDate = (dateString: any) => {
    if (!dateString) return '-';
    try {
      // Ellenőrizzük, hogy Timestamp objektum-e
      if (dateString && typeof dateString === 'object' && 'toDate' in dateString) {
        try {
          const date = dateString.toDate();
          if (isNaN(date.getTime())) return '-';
          return format(date, 'yyyy-MM-dd');
        } catch (e) {
          return '-';
        }
      }
      
      // Ha Date objektum
      if (dateString instanceof Date) {
        if (isNaN(dateString.getTime())) return '-';
        return format(dateString, 'yyyy-MM-dd');
      }
      
      // Ha string, ellenőrizzük, hogy a dátum string helyes formátumú-e
      if (typeof dateString === 'string') {
        if (!dateString.match(/^\d{4}-\d{2}-\d{2}/) && 
            !dateString.match(/^\d{4}\/\d{2}\/\d{2}/) && 
            !dateString.match(/^\d{4}\.\d{2}\.\d{2}/)) {
          return dateString;
        }
        
        const date = new Date(dateString);
        // Ellenőrizzük, hogy érvényes dátum-e
        if (isNaN(date.getTime())) {
          return dateString;
        }
        return format(date, 'yyyy-MM-dd');
      }
      
      return String(dateString);
    } catch (error) {
      console.error('Invalid date format:', dateString, error);
      return '-';
    }
  };

  // Biztonságos érték kiíró függvény
  const safeValue = (value: any, defaultValue = '-') => {
    if (value === undefined || value === null || value === '') {
      return defaultValue;
    }
    return value;
  };

  if (!quotation) {
    return null;
  }

  const organization = organizations?.find(org => org.id === quotation.organizationId);
  const customer = customers?.find(c => c.id === quotation.customerId);
  const quotationProducts = products?.filter(p =>
    quotation.items.some(item => item.productId === p.id)
  );

  const calculateTotal = () => {
    if (!quotation.items || !Array.isArray(quotation.items) || quotation.items.length === 0) {
      return 0;
    }
    
    return quotation.items.reduce((sum, item) => {
      const itemTotal = item.totalWithTax || (item.price * item.quantity * (1 + item.taxRate / 100));
      return sum + itemTotal;
    }, 0);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={quotation.quotationNumber || quotation.number || t('quotations.viewDetails')}
      size="4xl"
    >
      <div className="p-4 space-y-4">
        {/* Alapadatok */}
        <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
          <h3 className="text-sm font-medium text-gray-700 mb-3">
            {t('quotations.form.basicDetails.title')}
          </h3>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <span className="text-xs text-gray-500">{t('quotations.fields.organization')}</span>
              <p className="text-sm text-gray-800">{organization?.name || '-'}</p>
            </div>

            <div>
              <span className="text-xs text-gray-500">{t('quotations.fields.customer')}</span>
              <p className="text-sm text-gray-800">
                {customer?.type === 'organization'
                  ? customer.companyName
                  : customer ? `${customer.firstName} ${customer.lastName}` : '-'}
              </p>
            </div>

            <div>
              <span className="text-xs text-gray-500">{t('quotations.fields.status')}</span>
              <p className="text-sm text-gray-800">{t(`quotations.status.${quotation.status}`)}</p>
            </div>

            <div>
              <span className="text-xs text-gray-500">{t('quotations.fields.validUntil')}</span>
              <p className="text-sm text-gray-800">
                {formatSafeDate(quotation.validUntil)}
              </p>
            </div>
          </div>
        </div>

        {/* Cég adatok */}
        <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
          <h3 className="text-sm font-medium text-gray-700 mb-3">
            {t('quotations.form.sections.companyDetails.title')}
          </h3>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <span className="text-xs text-gray-500">{t('quotations.form.sections.companyDetails.quotationNumber')}</span>
              <p className="text-sm text-gray-800">{safeValue(quotation.quotationNumber || quotation.number)}</p>
            </div>

            <div>
              <span className="text-xs text-gray-500">{t('quotations.form.sections.companyDetails.companyName')}</span>
              <p className="text-sm text-gray-800">{safeValue(quotation.companyName)}</p>
            </div>

            <div>
              <span className="text-xs text-gray-500">{t('quotations.form.sections.companyDetails.companyAddress')}</span>
              <p className="text-sm text-gray-800">{safeValue(quotation.companyAddress)}</p>
            </div>

            <div>
              <span className="text-xs text-gray-500">{t('quotations.form.sections.companyDetails.taxNumber')}</span>
              <p className="text-sm text-gray-800">{safeValue(quotation.taxNumber)}</p>
            </div>

            <div>
              <span className="text-xs text-gray-500">{t('quotations.form.sections.companyDetails.registrationNumber')}</span>
              <p className="text-sm text-gray-800">{safeValue(quotation.registrationNumber)}</p>
            </div>
          </div>
        </div>

        {/* Kapcsolattartási és árazási adatok */}
        <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
          <h3 className="text-sm font-medium text-gray-700 mb-3">
            {t('quotations.form.sections.contactAndPricing.title')}
          </h3>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <span className="text-xs text-gray-500">{t('customers.form.contactDetails.representativeName') || 'Képviselő neve'}</span>
              <p className="text-sm text-gray-800">{safeValue(quotation.representativeName)}</p>
            </div>

            <div>
              <span className="text-xs text-gray-500">{t('quotations.form.sections.contactAndPricing.contactName')}</span>
              <p className="text-sm text-gray-800">{safeValue(quotation.contactName)}</p>
            </div>

            <div>
              <span className="text-xs text-gray-500">{t('quotations.form.sections.contactAndPricing.date')}</span>
              <p className="text-sm text-gray-800">
                {formatSafeDate(quotation.date)}
              </p>
            </div>

            <div>
              <span className="text-xs text-gray-500">{t('quotations.form.sections.contactAndPricing.paymentDue')}</span>
              <p className="text-sm text-gray-800">
                {quotation.paymentDue ? `${quotation.paymentDue}` : '-'}
              </p>
            </div>

            <div>
              <span className="text-xs text-gray-500">{t('quotations.form.sections.contactAndPricing.price')}</span>
              <p className="text-sm text-gray-800">{safeValue(quotation.price)}</p>
            </div>

            <div>
              <span className="text-xs text-gray-500">{t('quotations.form.sections.contactAndPricing.priceUnit')}</span>
              <p className="text-sm text-gray-800">{safeValue(quotation.priceUnit)}</p>
            </div>
          </div>
        </div>

        {/* Fogyasztási és szerződési adatok */}
        <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
          <h3 className="text-sm font-medium text-gray-700 mb-3">
            {t('quotations.form.sections.consumptionAndContract.title')}
          </h3>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <span className="text-xs text-gray-500">{t('quotations.form.sections.consumptionAndContract.consumption')}</span>
              <p className="text-sm text-gray-800">{safeValue(quotation.consumption)}</p>
            </div>

            <div>
              <span className="text-xs text-gray-500">{t('quotations.form.sections.consumptionAndContract.contractStart')}</span>
              <p className="text-sm text-gray-800">
                {formatSafeDate(quotation.contractStart)}
              </p>
            </div>

            <div>
              <span className="text-xs text-gray-500">{t('quotations.form.sections.consumptionAndContract.contractEnd')}</span>
              <p className="text-sm text-gray-800">
                {formatSafeDate(quotation.contractEnd)}
              </p>
            </div>
          </div>
        </div>

        {/* Teljes áramköltség számítás */}
        <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
          <h3 className="text-sm font-medium text-gray-700 mb-3">
            {t('quotations.form.sections.consumptionAndContract.estimatedCost')}
          </h3>
          <div className="space-y-2">
            {quotation.price && quotation.consumption && (
              <div className="flex flex-col">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm text-gray-600">{t('quotations.form.sections.consumptionAndContract.price')}:</span>
                  <span className="text-sm text-gray-800">{quotation.price} {safeValue(quotation.priceUnit)}</span>
                </div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm text-gray-600">{t('quotations.form.sections.consumptionAndContract.consumption')}:</span>
                  <span className="text-sm text-gray-800">{quotation.consumption} kWh</span>
                </div>
                <div className="flex justify-between items-center pt-2 border-t border-gray-200 mt-2">
                  <span className="text-sm font-medium text-gray-700">{t('quotations.form.sections.consumptionAndContract.estimatedAllCost')}:</span>
                  <span className="text-sm font-bold text-primary-600">
                    {(() => {
                      try {
                        const price = parseFloat(quotation.price);
                        const consumption = parseFloat(quotation.consumption);
                        let totalCost = 0;
                        let currency = 'HUF';

                        // Ellenőrzöm az árformátumot és számolom a teljes költséget
                        if (quotation.priceUnit?.includes('EUR/MWh')) {
                          // EUR/MWh formátum - a fogyasztást MWh-ba kell konvertálni
                          totalCost = price * (consumption / 1000);
                          currency = 'EUR';
                        } else if (quotation.priceUnit?.includes('HUF/kWh')) {
                          // HUF/kWh formátum - közvetlen szorzás
                          totalCost = price * consumption;
                          currency = 'HUF';
                        } else {
                          // Alapértelmezett eset
                          totalCost = price * consumption;
                        }

                        return new Intl.NumberFormat('hu-HU', {
                          style: 'currency',
                          currency: currency,
                          maximumFractionDigits: 2
                        }).format(totalCost);
                      } catch (e) {
                        console.error("Számítási hiba:", e);
                        return "Nem számítható";
                      }
                    })()}
                  </span>
                </div>
              </div>
            )}
            {(!quotation.price || !quotation.consumption) && (
              <p className="text-sm text-gray-500">{t('quotations.form.sections.consumptionAndContract.noData')}</p>
            )}
          </div>
        </div>

        {/* KÁT szöveg */}
        {quotation.kat?.text && (
          <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
            <h3 className="text-sm font-medium text-gray-700 mb-3">
              {t('quotations.form.sections.kat.title')}
            </h3>
            <p className="text-sm text-gray-800">{quotation.kat.text}</p>
          </div>
        )}

        {/* POD adatok */}
        {quotation.pod && (
          <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
            <h3 className="text-sm font-medium text-gray-700 mb-3">
              {t('quotations.form.sections.pod.title')}
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <span className="text-xs text-gray-500">{t('quotations.form.sections.pod.podId')}</span>
                <p className="text-sm text-gray-800">{safeValue(quotation.pod?.id)}</p>
              </div>

              <div>
                <span className="text-xs text-gray-500">{t('quotations.form.sections.pod.podAddress')}</span>
                <p className="text-sm text-gray-800">{safeValue(quotation.pod?.address)}</p>
              </div>

              <div>
                <span className="text-xs text-gray-500">{t('quotations.form.sections.pod.measurementType')}</span>
                <p className="text-sm text-gray-800">{safeValue(quotation.pod?.measurementType)}</p>
              </div>

              <div>
                <span className="text-xs text-gray-500">{t('quotations.form.sections.pod.annualConsumption')}</span>
                <p className="text-sm text-gray-800">{safeValue(quotation.pod?.annualConsumption)}</p>
              </div>

              <div>
                <span className="text-xs text-gray-500">{t('quotations.form.sections.pod.power')}</span>
                <p className="text-sm text-gray-800">{safeValue(quotation.pod?.power)}</p>
              </div>

              <div>
                <span className="text-xs text-gray-500">{t('quotations.form.sections.pod.profileNumber')}</span>
                <p className="text-sm text-gray-800">{safeValue(quotation.pod?.profileNumber)}</p>
              </div>
            </div>
          </div>
        )}

        {/* További POD-ok */}
        {quotation.additionalPods && quotation.additionalPods.length > 0 && (
          <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
            <h3 className="text-sm font-medium text-gray-700 mb-3">
              {t('quotations.form.sections.additionalPods.title')}
            </h3>

            <div className="space-y-4">
              {quotation.additionalPods.map((pod, index) => (
                <div key={index} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <span className="text-xs text-gray-500">{t('quotations.form.sections.additionalPods.podId', { index: index + 2 })}</span>
                    <p className="text-sm text-gray-800">{safeValue(pod.id)}</p>
                  </div>

                  <div>
                    <span className="text-xs text-gray-500">{t('quotations.form.sections.additionalPods.podAddress', { index: index + 2 })}</span>
                    <p className="text-sm text-gray-800">{safeValue(pod.address)}</p>
                  </div>

                  <div>
                    <span className="text-xs text-gray-500">{t('quotations.form.sections.additionalPods.measurementType', { index: index + 2 })}</span>
                    <p className="text-sm text-gray-800">{safeValue(pod.measurementType)}</p>
                  </div>

                  <div>
                    <span className="text-xs text-gray-500">{t('quotations.form.sections.additionalPods.annualConsumption', { index: index + 2 })}</span>
                    <p className="text-sm text-gray-800">{safeValue(pod.annualConsumption)}</p>
                  </div>

                  <div>
                    <span className="text-xs text-gray-500">{t('quotations.form.sections.additionalPods.power', { index: index + 2 })}</span>
                    <p className="text-sm text-gray-800">{safeValue(pod.power)}</p>
                  </div>

                  <div>
                    <span className="text-xs text-gray-500">{t('quotations.form.sections.additionalPods.profileNumber', { index: index + 2 })}</span>
                    <p className="text-sm text-gray-800">{safeValue(pod.profileNumber)}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Tételek - megjelenítés ha van */}
        {quotation.items && quotation.items.length > 0 && (
          <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
            <h3 className="text-sm font-medium text-gray-700 mb-3">
              {t('quotations.form.sections.items.title')}
            </h3>

            <div className="space-y-2">
              {quotation.items.map((item, index) => {
                const product = quotationProducts?.find(p => p.id === item.productId);
                return (
                  <div
                    key={index}
                    className="flex items-center justify-between p-2 bg-gray-50 rounded-lg border border-gray-200"
                  >
                    <div className="flex items-center">
                      <DocumentTextIcon className="w-5 h-5 text-primary-600 mr-2" />
                      <div>
                        <span className="text-sm text-gray-800">
                          {product?.name || item.productName || `Termék ${index + 1}`}
                        </span>
                        <span className="text-xs text-gray-500 block">
                          {item.quantity} x {new Intl.NumberFormat('hu-HU', {
                            style: 'currency',
                            currency: item.currency || 'HUF'
                          }).format(item.price)}
                        </span>
                      </div>
                    </div>
                    <div className="text-sm text-gray-800">
                      {new Intl.NumberFormat('hu-HU', {
                        style: 'currency',
                        currency: item.currency || 'HUF'
                      }).format(item.totalWithTax || (item.price * item.quantity * (1 + item.taxRate / 100)))}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-700">{t('quotations.fields.total')}</span>
                <span className="text-lg font-medium text-gray-800">
                  {new Intl.NumberFormat('hu-HU', {
                    style: 'currency',
                    currency: quotation.items[0]?.currency || 'HUF'
                  }).format(calculateTotal())}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Dokumentumok */}
        {quotation.documents && quotation.documents.length > 0 && (
          <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
            <h3 className="text-sm font-medium text-gray-700 mb-3">
              {t('quotations.form.sections.documents.title')}
            </h3>

            <div className="space-y-2">
              {quotation.documents.map((document, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-2 bg-gray-50 rounded-lg border border-gray-200"
                >
                  <div className="flex items-center">
                    <DocumentIcon className="w-5 h-5 text-primary-600 mr-2" />
                    <span className="text-sm text-gray-800">{document.name}</span>
                  </div>
                  <a
                    href={document.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-primary-600 hover:text-primary-700"
                  >
                    {t('common.download')}
                  </a>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Megjegyzések */}
        {quotation.notes && (
          <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
            <h3 className="text-sm font-medium text-gray-700 mb-3">
              {t('quotations.form.notes.title')}
            </h3>
            <p className="text-sm text-gray-800">{quotation.notes}</p>
          </div>
        )}

        <div className="flex justify-end space-x-2">
          <button
            onClick={() => {
              onClose();
              onEdit(quotation);
            }}
            className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-md text-sm flex items-center"
          >
            <PencilSquareIcon className="w-4 h-4 mr-2" />
            {t('common.edit')}
          </button>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-md text-sm"
          >
            {t('common.close')}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default QuotationViewModal; 